import axios, { type AxiosInstance } from 'axios';
import {
  type BrowserLoginSuccess,
  LOGIN_METHOD,
  type LoginChallenge,
  type LoginChallengeUserResponse,
  type LoginRequest,
  loginMethodFromTypeOrThrow,
  type Verify2FALoginRequest,
  type WhoamiResponse,
} from './types';
import type { Session } from './types/session';
import type { LoginChallengeResult } from './types/state';
import {
  clearLsSession,
  getLsLoginSession,
  getLsSession,
  hasFutureExpiry,
  makeAuthHeaders,
  setLsSession,
} from './utils';

export interface RustyAuthApiProps {
  baseUrl?: string;
  timeout?: number;
  useLocalStore?: boolean;
  localStorageKey?: string;
  /**
   * Optional callback fired whenever the in-memory session changes —
   * `setSession` / `clearSession` / `whoami` refreshes / `refresh` /
   * `applyLoginSuccess`. Lets host apps register cross-cutting side effects
   * (Sentry user context, analytics identify, etc.) in one place so they
   * fire regardless of which code path triggered the change.
   *
   * `null` means the session was cleared (logged out).
   */
  onSessionChange?: (session: Session | null) => void;
}

/**
 * Defensive guard for cross-origin URLs the backend hands us before we feed
 * them to `window.location.assign`. Used for URLs that legitimately point
 * off-origin (e.g. the OIDC RP-initiated logout URL from
 * `/a/auth/oidc/end-session-url`).
 *
 * Allow rules — keep this list deliberately small:
 *   - Must parse as an absolute URL via the `URL` constructor.
 *   - Scheme must be `https:` or `http:`. This is the meaningful filter
 *     against the actual attack surface: `javascript:`, `data:`, `blob:`,
 *     `file:`, custom-scheme deep links.
 *
 * For URLs that should stay on our own origin (e.g. a `return_to` after
 * login), use [`isSafeSameOriginTarget`] instead.
 */
export const isSafeRedirectTarget = (candidate: string): boolean => {
  let parsed: URL;
  try {
    parsed = new URL(candidate);
  } catch {
    return false;
  }
  return parsed.protocol === 'https:' || parsed.protocol === 'http:';
};

/**
 * Stricter variant of [`isSafeRedirectTarget`] for URLs that must stay on the
 * SPA's own origin. Use for post-login `return_to` and similar intra-app
 * navigation targets. Accepts absolute URLs whose origin matches
 * `window.location.origin`, and relative paths (resolved against the current
 * origin). Rejects everything else, including off-origin https URLs.
 */
export const isSafeSameOriginTarget = (candidate: string): boolean => {
  try {
    const parsed = new URL(candidate, window.location.origin);
    if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') return false;
    return parsed.origin === window.location.origin;
  } catch {
    return false;
  }
};

// The backend sends RFC 3339 strings (chrono::DateTime<Utc>); the
// rest of the frontend stores expiries as unix seconds.
const rfc3339ToUnixSeconds = (value: string): number => {
  const ms = Date.parse(value);
  if (Number.isNaN(ms)) {
    throw new Error(`Invalid RFC 3339 datetime from backend: ${value}`);
  }
  return Math.floor(ms / 1000);
};

/**
 * Convert a `BrowserLoginSuccess` payload into the unix-second expiry the
 * frontend session uses internally. Split-token sessions only expose the
 * access-token expiry; refresh is a server-side concern keyed on the HttpOnly
 * cookie.
 */
export const sessionExpiriesFromLoginSuccess = (
  ls: BrowserLoginSuccess
): { accessTokenExpiresAt: number } => {
  if (!ls.access_token_expires_at) {
    throw new Error('BrowserLoginSuccess.access_token_expires_at missing — backend is out of date');
  }
  return {
    accessTokenExpiresAt: rfc3339ToUnixSeconds(ls.access_token_expires_at),
  };
};

export interface RustyAuthSpec {
  /** Initiate login process with email, NOSTR, or other methods */
  login(loginRequest: LoginRequest): Promise<LoginChallenge>;
  /**
   * Complete login challenge. The success branch funnels through
   * the auth-code flow; the SPA exchanges the code via /v1/login/exchange
   * before a session is established. Returns the raw envelope so callers
   * can fork on `status`.
   */
  loginChallenge(props: LoginChallengeUserResponse): Promise<LoginChallengeResult>;
  /**
   * Verify 2FA code. Returns the auth-code envelope; the SPA must
   * exchange to actually populate the session.
   */
  verify2FA(request: Verify2FALoginRequest): Promise<BrowserLoginSuccess>;
  /**
   * Exchange an auth code minted by /login/challenge (or the 2FA endpoint, or
   * an OAuth callback) for a `BrowserLoginSuccess`. The browser session
   * cookie is attached server-side via the `Set-Cookie` header; the SPA
   * keeps only the access token and identity claims.
   */
  exchangeAuthCode(code: string, method?: LOGIN_METHOD): Promise<BrowserLoginSuccess>;
  /**
   * Refresh the short-lived access token. Browser callers rely on the
   * `__Secure-session` cookie (sent automatically with `withCredentials`).
   * On 401/403 the session is cleared. Transient failures throw and let
   * callers back off.
   */
  refresh(): Promise<BrowserLoginSuccess | undefined>;
  /** Refreshes an expired access token of a signed-in session first; never throws. */
  ensureFreshToken(): Promise<void>;
  /**
   * Fetch fresh identity claims from /v1/a/whoami and merge them into the
   * session. Cheap (Redis-cached server-side); safe to call on focus.
   */
  whoami(): Promise<WhoamiResponse>;
  /** Log out user and invalidate session; `true` when the browser is handed to the IdP */
  logout(): Promise<boolean>;
  /** Get current session data */
  getSession(): Session;
  /** Set/update session data */
  setSession(session: Session): void;
  /** Clear session data */
  clearSession(): void;
  /** Check if access token is valid and not expired */
  hasValidAccessToken(threshold?: number): boolean;
  /** Get access token (may be expired) */
  getAccessToken(): string;
  /**
   * Initiate a silent OIDC re-auth that returns the user to a given URL
   *  after the round-trip. Used to refresh local profile data from the IdP
   *  without losing the user's place in the UI.
   */
  initiateOidcRefresh(returnTo: string): Promise<string>;
}

export class RustyAuth implements RustyAuthSpec {
  private baseUrl: string;
  private timeout: number;
  private useLocalStore: boolean = false;
  private localStorageKey: string;
  private client: AxiosInstance;
  private onSessionChange?: (session: Session | null) => void;
  // Concurrent callers share one refresh; the session cookie rotates on each.
  private refreshInFlight: Promise<BrowserLoginSuccess | undefined> | null = null;

  private session: Session;

  constructor({
    baseUrl,
    timeout,
    useLocalStore,
    localStorageKey,
    onSessionChange,
  }: RustyAuthApiProps) {
    this.baseUrl = baseUrl || 'https://api.formshive.com/v1';
    this.timeout = timeout || 5000;
    this.useLocalStore = useLocalStore || false;
    if (localStorageKey) {
      this.localStorageKey = localStorageKey;
    } else {
      throw new Error('localStorageKey is required');
    }
    this.onSessionChange = onSessionChange;

    this.client = axios.create({
      baseURL: this.baseUrl,
      timeout: this.timeout,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.session = {
      isLoggedIn: false,
    };

    console.info(`[api] => baseUrl: ${this.baseUrl}`);

    if (this.useLocalStore) {
      console.info('[api] => Using local storage for session management');
      const storedSession = getLsSession(this.localStorageKey);
      if (storedSession) {
        this.session = storedSession;
        // Never log the session itself — it carries the JWT access token.
        console.debug('[api] => Loaded session from local storage');
      } else {
        console.debug('[api] => No session found in local storage');
      }
    }
  }

  getSession(): Session {
    return this.session;
  }

  setSession(session: Session): void {
    this.session = session;
    if (this.useLocalStore) {
      setLsSession(session, this.localStorageKey);
    }
    try {
      this.onSessionChange?.(session);
    } catch (e) {
      console.warn('AUTH: onSessionChange callback threw; continuing', e);
    }
  }

  clearSession(): void {
    if (this.useLocalStore) {
      clearLsSession(this.localStorageKey);
    }
    this.session = { isLoggedIn: false };
    try {
      this.onSessionChange?.(null);
    } catch (e) {
      console.warn('AUTH: onSessionChange callback threw; continuing', e);
    }
  }

  hasValidAccessToken(threshold: number = 30): boolean {
    if (!this.session.isLoggedIn) {
      console.warn('AUTH: Session is not logged in');
      return false;
    }

    if (this.session.accessTokenExpiresAt === undefined) {
      console.warn('AUTH: Access token expiration time is not defined');
      return false;
    }

    if (!hasFutureExpiry(this.session.accessTokenExpiresAt, threshold)) {
      console.warn(
        `AUTH: Access token is not valid, expires at ${this.session.accessTokenExpiresAt}`
      );
      return false;
    }

    return true;
  }

  getUserId(): string {
    if (!this.session.userId) {
      throw new Error('No user id');
    }
    return this.session.userId;
  }

  getAccessToken(): string {
    if (!this.hasValidAccessToken()) {
      throw new Error('No access token');
    }
    return this.session.accessToken as string;
  }

  async login(loginRequest: LoginRequest): Promise<LoginChallenge> {
    switch (loginRequest.type) {
      case LOGIN_METHOD.NOSTR:
        this.setSession({
          isLoggedIn: false,
          publicKey: loginRequest.content.public_key,
          method: LOGIN_METHOD.NOSTR,
        });
        break;
      case LOGIN_METHOD.EMAIL_MAGIC_LINK:
        this.setSession({
          isLoggedIn: false,
          method: LOGIN_METHOD.EMAIL_MAGIC_LINK,
        });
        break;
      case LOGIN_METHOD.OIDC:
        this.setSession({
          isLoggedIn: false,
          method: LOGIN_METHOD.OIDC,
        });
        break;
      default:
        throw new Error(`Unsupported login method: ${loginRequest.type}`);
    }

    // browser must accept the Set-Cookie oauth_state binding for OAuth/OIDC
    const { data } = await this.client.post<LoginChallenge>('/login', loginRequest, {
      withCredentials: true,
    });
    return data;
  }

  /**
   * The challenge endpoint funnels success through the auth-code flow. On
   * success we transparently exchange the code so the caller sees a populated
   * session. Callers that need to fork on the raw envelope (2FA prompt) can
   * inspect the returned status.
   */
  async loginChallenge(props: LoginChallengeUserResponse): Promise<LoginChallengeResult> {
    const { data } = await this.client.post<LoginChallengeResult>('/login/challenge', props);

    if (data.status === 'success') {
      const method = loginMethodFromTypeOrThrow(props.type);
      // Preserve the public key the user supplied at the /login step so the
      // post-exchange session keeps the NOSTR identity claim.
      let publicKey: string | undefined;
      if (method === LOGIN_METHOD.NOSTR && this.useLocalStore) {
        const savedSession = getLsLoginSession(this.localStorageKey);
        if (savedSession?.publicKey) {
          publicKey = savedSession.publicKey;
        } else {
          console.warn(
            `No public key found in local storage for NOSTR login (${JSON.stringify(savedSession)})`
          );
        }
      }

      const browser = await this.exchangeAuthCode(data.code, method);
      this.applyLoginSuccess(browser, method, publicKey);
    } else if (!this.session.method) {
      // A magic link opened in another browser has no pending session for verify2FA to read.
      this.setSession({ isLoggedIn: false, method: loginMethodFromTypeOrThrow(props.type) });
    }

    return data;
  }

  async verify2FA(request: Verify2FALoginRequest): Promise<BrowserLoginSuccess> {
    const { data } = await this.client.post<LoginChallengeResult>('/login/2fa', request);

    if (data.status !== 'success') {
      // The 2FA endpoint can only return success. Anything else is a backend
      // contract violation.
      throw new Error(`AUTH: unexpected /login/2fa response status: ${(data as any).status}`);
    }

    const pendingSession = this.getSession();
    const method = pendingSession.method;
    if (!method) {
      throw new Error(
        'AUTH: 2FA verify with no pending method on session — login flow not initialised'
      );
    }
    const browser = await this.exchangeAuthCode(data.code, method);

    const publicKey = method === LOGIN_METHOD.NOSTR ? pendingSession.publicKey : undefined;
    this.applyLoginSuccess(browser, method, publicKey);
    return browser;
  }

  /**
   * Exchange an auth code for a `BrowserLoginSuccess`. Uses
   * `withCredentials: true` so the browser accepts the `Set-Cookie`
   * carrying the opaque session token.
   */
  async exchangeAuthCode(code: string, _method?: LOGIN_METHOD): Promise<BrowserLoginSuccess> {
    const { data } = await this.client.post<BrowserLoginSuccess>(
      '/login/exchange',
      { code },
      { withCredentials: true }
    );
    return data;
  }

  /**
   * Apply a `BrowserLoginSuccess` to the in-memory + persisted session,
   * preserving identity claims that aren't part of the response (e.g. the
   * NOSTR public key, which is client-supplied at /login time).
   */
  private applyLoginSuccess(
    data: BrowserLoginSuccess,
    method: LOGIN_METHOD,
    publicKey?: string
  ): void {
    const newSession: Session = {
      isLoggedIn: true,
      userId: data.user_id,
      accessToken: data.access_token,
      email: data.email,
      displayName: data.display_name,
      ...(publicKey !== undefined ? { publicKey } : {}),
      ...sessionExpiriesFromLoginSuccess(data),
      method,
    } as Session;
    this.setSession(newSession);
  }

  /**
   * Trigger a silent OIDC re-auth flow that lands the user back at
   * `returnTo`. POSTs to the OIDC login dispatcher with the return URL
   * stashed server-side, then returns the auth URL the caller redirects
   * the browser to.
   */
  async initiateOidcRefresh(returnTo: string): Promise<string> {
    const { data } = await this.client.post<LoginChallenge>('/login', {
      type: LOGIN_METHOD.OIDC,
      content: { return_to: returnTo },
    });
    if (data.type !== LOGIN_METHOD.OIDC) {
      throw new Error('AUTH: unexpected login challenge type from OIDC refresh');
    }
    return data.content.auth_url;
  }

  /**
   * Refresh the short-lived access token. The browser sends no body — the
   * `__Secure-session` HttpOnly cookie (path `/v1/a/`)
   * is the only refresh credential. `withCredentials: true` is required so
   * the cookie travels.
   *
   * On 401/403, the session is cleared locally; the caller is expected to
   * navigate to /login. Transient failures (5xx, network) throw without
   * clearing — callers can back off and retry.
   */
  refresh(): Promise<BrowserLoginSuccess | undefined> {
    this.refreshInFlight ??= this.refreshNow().finally(() => {
      this.refreshInFlight = null;
    });
    return this.refreshInFlight;
  }

  async ensureFreshToken(): Promise<void> {
    const expiresAt = this.session.accessTokenExpiresAt;
    if (!this.session.isLoggedIn || (expiresAt !== undefined && hasFutureExpiry(expiresAt, 30))) {
      return;
    }
    try {
      await this.refresh();
    } catch {
      // the request that waited fails on its own
    }
  }

  private async refreshNow(): Promise<BrowserLoginSuccess | undefined> {
    try {
      const { data } = await this.client.post<BrowserLoginSuccess>(
        '/login/refresh',
        {},
        { withCredentials: true }
      );

      // The pre-refresh session must carry a method; otherwise the local
      // state is corrupt (we should never end up with a session row that has
      // no method). Bail loudly rather than silently mislabelling the
      // refreshed session as Nostr.
      const method = this.session.method;
      if (!method) {
        console.warn('AUTH: refresh attempted with no session.method; clearing session');
        this.clearSession();
        throw new Error('AUTH: session has no method, refresh aborted');
      }
      const publicKey = method === LOGIN_METHOD.NOSTR ? this.session.publicKey : undefined;
      this.applyLoginSuccess(data, method, publicKey);
      console.info('AUTH: Token refresh successful');
      return data;
    } catch (error: any) {
      const status = error?.response?.status;
      if (status === 401 || status === 403) {
        console.warn(`AUTH: refresh rejected (${status}); clearing session`);
        this.clearSession();
        throw new Error('AUTH: Authentication failed, session cleared');
      }
      // Transient: leave session in place; caller decides whether to retry.
      console.warn('AUTH: refresh failed transiently; session preserved', error);
      throw error;
    }
  }

  /**
   * GET /v1/a/whoami — fresh identity claims for the current session.
   * Backend caches OIDC responses in Redis for 5min so this is cheap.
   *
   * `withCredentials: true` is required because the endpoint reads the
   * session cookie (and the access token bearer). Updates the local
   * session opportunistically with the freshest email / displayName.
   */
  async whoami(): Promise<WhoamiResponse> {
    const get = async () => {
      await this.ensureFreshToken();
      const headers = this.hasValidAccessToken()
        ? makeAuthHeaders(this.getAccessToken())
        : undefined;
      return this.client.get<WhoamiResponse>('/a/whoami', { withCredentials: true, headers });
    };
    let response: Awaited<ReturnType<typeof get>>;
    try {
      response = await get();
    } catch (error) {
      // Same single 401 retry as the `/a/` requests in RustyFormsApi.
      const status = (error as { response?: { status?: number } })?.response?.status;
      if (status !== 401 || !this.session.isLoggedIn) throw error;
      const refreshed = await this.refresh().then(
        () => true,
        () => false
      );
      if (!refreshed) throw error;
      response = await get();
    }
    const { data } = response;

    if (this.session.isLoggedIn) {
      this.setSession({
        ...this.session,
        email: data.email ?? this.session.email,
        displayName: data.display_name ?? this.session.displayName,
      } as Session);
    }

    return data;
  }

  /**
   * Logout sequence (frontend #7 ordering):
   *   1. OIDC only: fetch end-session URL while the cookie + access token
   *      are still valid. Backend reads the id_token from the session row,
   *      so no `id_token_hint` query param.
   *   2. POST /v1/a/logout — backend deletes the row, revokes IdP refresh,
   *      clears the cookie via Set-Cookie Max-Age=0.
   *   3. Clear local session state.
   *   4. OIDC only: navigate to the end-session URL.
   *
   * Resolves `true` once the browser is on its way to the IdP. Callers must
   * not navigate after that: a second navigation cancels the end-session
   * request and leaves the IdP session alive.
   */
  async logout(): Promise<boolean> {
    const isOidc = this.session.method === LOGIN_METHOD.OIDC;
    // The logout endpoint needs a live bearer; an idle tab's token has expired.
    await this.ensureFreshToken();
    const authHeaders = this.hasValidAccessToken()
      ? makeAuthHeaders(this.getAccessToken())
      : undefined;

    let endSessionUrl: string | null = null;
    if (isOidc) {
      try {
        const { data } = await this.client.get<{ url: string | null }>(
          '/a/auth/oidc/end-session-url',
          {
            withCredentials: true,
            headers: authHeaders,
          }
        );
        // `null`: the IdP has no end-session endpoint, so logout stays local.
        endSessionUrl = data.url;
      } catch (error) {
        console.error(
          'AUTH: failed to fetch OIDC end-session URL; proceeding with local logout.',
          error
        );
      }
    }

    try {
      await this.client.post<null>(
        '/a/logout',
        {},
        { withCredentials: true, headers: authHeaders }
      );
    } catch (error) {
      // Always proceed to clear local state — the server-side row may already
      // be gone (e.g. cron expiry) and we should never leave a stale session
      // glued to the SPA.
      console.error('AUTH: /a/logout failed; clearing session regardless', error);
    }

    this.clearSession();

    if (endSessionUrl) {
      // Re-validate the URL before handing it to the browser. Catches a
      // misconfigured `frontend_url` on the backend or any future bug that
      // would emit a non-IdP URL through `/a/auth/oidc/end-session-url`.
      if (isSafeRedirectTarget(endSessionUrl)) {
        window.location.assign(endSessionUrl);
        return true;
      }
      console.error('AUTH: refusing unsafe end-session URL; falling back to local logout.', {
        endSessionUrl,
      });
    }
    return false;
  }
}
